// =====================================================
// HOOK: useMyTasks
// Description: Hook pour récupérer les tâches assignées à l'utilisateur connecté
// =====================================================

import { useMemo, useCallback, useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { useCRUD } from './useCRUD';
import { useSupabase } from './useSupabase';
import type { Task } from '../types/task';
import type { TaskWithEntity } from '../components/tasks';
import { getEntityDisplayName } from '../lib/timeline-logger';

/**
 * Hook pour récupérer toutes les tâches assignées à l'utilisateur connecté
 * Retourne les tâches avec enrichissement des informations utilisateur
 */
export function useMyTasks() {
  const { user } = useAuth();
  const userId = user?.id;
  const supabase = useSupabase();
  const [enrichedTasks, setEnrichedTasks] = useState<TaskWithEntity[]>([]);

  const crud = useCRUD<Task>('tasks', {
    select: '*',
    orderBy: { column: 'due_date', ascending: true },
  });

  // Filtrer les tâches assignées à l'utilisateur connecté
  const myTasks = useMemo(() => {
    if (!userId) return [];
    return crud.data.filter((task) => task.assigned_to === userId);
  }, [crud.data, userId]);

  // Enrichir les tâches avec les noms des entités concernées
  useEffect(() => {
    async function enrichTasks() {
      if (myTasks.length === 0) {
        setEnrichedTasks([]);
        return;
      }

      const enriched = await Promise.all(
        myTasks.map(async (task) => {
          let entity_name = '';

          try {
            if (task.entity_type === 'contact') {
              const { data } = await supabase
                .from('contacts')
                .select('first_name, last_name')
                .eq('id', task.entity_id)
                .single();

              if (data) {
                entity_name = `${data.first_name} ${data.last_name}`;
              }
            } else if (task.entity_type === 'crew') {
              const { data } = await supabase
                .from('crews')
                .select('name')
                .eq('id', task.entity_id)
                .single();

              if (data) {
                entity_name = data.name;
              }
            }
          } catch (error) {
            console.error('Error enriching task:', error);
          }

          return {
            ...task,
            entity_name,
          };
        })
      );

      setEnrichedTasks(enriched);
    }

    enrichTasks();
  }, [myTasks, supabase]);

  // Séparer les tâches en complétées et en attente
  const pendingTasks = useMemo(
    () => enrichedTasks.filter((task) => !task.completed),
    [enrichedTasks]
  );

  const completedTasks = useMemo(
    () => enrichedTasks.filter((task) => task.completed),
    [enrichedTasks]
  );

  // Calculer le nombre de tâches urgentes (échéance dans moins de 3 jours)
  const urgentTasks = useMemo(() => {
    const threeDaysFromNow = new Date();
    threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);

    return pendingTasks.filter((task) => {
      const dueDate = new Date(task.due_date);
      return dueDate <= threeDaysFromNow;
    });
  }, [pendingTasks]);

  /**
   * Marquer une tâche comme complétée et logger dans la timeline
   */
  const completeTask = useCallback(
    async (taskId: string): Promise<boolean> => {
      const task = enrichedTasks.find((t) => t.id === taskId);
      if (!task) return false;

      const success = await crud.update(taskId, {
        completed: true,
        completed_at: new Date().toISOString(),
      } as Partial<Task>);

      if (success) {
        // ✅ Récupérer le nom de l'entité pour un contexte clair
        const entityName = await getEntityDisplayName(supabase, task.entity_type, task.entity_id);
        const titlePrefix = entityName ? `${entityName} - ` : '';

        // Logger dans timeline
        await supabase.from('timeline_events').insert({
          entity_type: task.entity_type,
          entity_id: task.entity_id,
          event_type: 'task_completed',
          title: `${titlePrefix}Tâche terminée : ${task.title}`,
          description: null,
          created_by: userId,
          metadata: {
            task_id: taskId,
            completed_at: new Date().toISOString(),
            entity_name: entityName,
          },
        });
      }

      return success;
    },
    [enrichedTasks, crud, supabase, userId]
  );

  /**
   * Supprimer une tâche
   */
  const deleteTask = useCallback(
    async (taskId: string): Promise<boolean> => {
      return await crud.remove(taskId);
    },
    [crud]
  );

  return {
    tasks: enrichedTasks,
    pendingTasks,
    completedTasks,
    urgentTasks,
    loading: crud.loading,
    error: crud.error,
    completeTask,
    deleteTask,
    refresh: crud.refresh,
  };
}
